// Saving typed changes a moment after typing stops, never keeping them only on
// screen. Shared by every text box and the area editor (use-deferred-save.ts),
// so the iPad and the desk save the same way.

/** Wait this long after the last change, then save. */
export const SAVE_DELAY_MS = 400;

export interface DeferredSave<T> {
  /** Save `value` after the pause — straight away when the page is already hidden. */
  schedule(value: T): void;
  /** Save what is waiting now; resolves once it, and any save already under way, is written. */
  flush(): Promise<void>;
  /** Drop what is waiting (a save already under way still finishes). */
  cancel(): void;
  /** Something is waiting, or still being written. */
  pending(): boolean;
}

const pageHidden = () => typeof document !== 'undefined' && document.visibilityState === 'hidden';

/**
 * `combine` joins a waiting value with a newer one (the area editor's changed
 * columns); by default the newer one replaces it. Saves run one after another,
 * in order. A page already hidden saves at once: iOS can end a minimised app
 * before the pause is over (OFFLINE.md test 2), and a box that mounted after
 * the page's other listeners hears about the hide last.
 */
export function createDeferredSave<T>(
  save: (value: T) => void | Promise<void>,
  combine: (older: T, newer: T) => T = (_older, newer) => newer,
  isHidden: () => boolean = pageHidden,
): DeferredSave<T> {
  let waiting: { value: T } | null = null;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let writes = 0;
  let chain: Promise<void> = Promise.resolve();

  function stopTimer() {
    clearTimeout(timer);
    timer = undefined;
  }

  /** The save starts here and now, so whoever saved can await the screen's next save behind it. */
  function start(value: T): Promise<void> {
    try {
      return Promise.resolve(save(value));
    } catch (err) {
      return Promise.reject(err);
    }
  }

  function flush(): Promise<void> {
    stopTimer();
    if (!waiting) return chain;
    const { value } = waiting;
    waiting = null;
    // Nothing under way: start now (a box left by tapping Duplicate has saved before Duplicate runs); else queue behind it.
    const write = writes === 0 ? start(value) : chain.then(() => start(value));
    writes += 1;
    chain = write
      .catch(() => undefined)
      .finally(() => {
        writes -= 1;
      });
    return chain;
  }

  return {
    schedule(value) {
      waiting = { value: waiting ? combine(waiting.value, value) : value };
      stopTimer();
      if (isHidden()) void flush();
      else timer = setTimeout(() => void flush(), SAVE_DELAY_MS);
    },
    flush,
    cancel() {
      stopTimer();
      waiting = null;
    },
    pending: () => waiting !== null || writes > 0,
  };
}
